import { request } from "./transport";

export interface AnalyticsByUnderlyingRow {
  readonly underlying: string;
  readonly count: number;
  readonly activeDays: number;
  readonly pnl: number;
  readonly winRate: number;
  readonly realizedBatches: readonly AnalyticsRealizedBatch[];
}

export interface AnalyticsRealizedBatch {
  readonly id: string;
  readonly openedOn: string | null;
  readonly closedOn: string;
  readonly pnl: number;
}

export interface AnalyticsByDayRow {
  readonly day: number;
  readonly trades: number;
  readonly winRate: number;
  readonly avgPnl: number;
}

export interface AnalyticsPerformance {
  readonly sharpe: number;
  readonly sortino: number | null;
  readonly winRate: number;
  readonly tradingDays: number;
}

export interface AnalyticsStreaks {
  readonly current: number;
  readonly bestWin: number;
  readonly worstLoss: number;
}

export interface AnalyticsEquityPoint {
  readonly date: string;
  readonly pnl: number;
  readonly cumulative: number;
  readonly trades: number;
  readonly underlyings: number;
  readonly breakdown: readonly {
    readonly underlying: string;
    readonly trades: number;
    readonly pnl: number;
  }[];
}

export interface AnalyticsMonthlyPoint {
  readonly month: string;
  readonly pnl: number;
  readonly breakdown: readonly {
    readonly underlying: string;
    readonly trades: number;
    readonly pnl: number;
  }[];
}

export interface AnalyticsDrawdownPoint {
  readonly date: string;
  readonly dd: number;
}

export interface AnalyticsMonthlyRiskPoint {
  readonly month: string;
  readonly pnl: number;
  readonly sharpe: number | null;
  readonly sortino: number | null;
  readonly downsideDeviation: number;
  readonly tradingDays: number;
}

export interface AnalyticsInstrumentImpactRow {
  readonly underlying: string;
  readonly trades: number;
  readonly activeDays: number;
  readonly pnl: number;
  readonly impact: number;
  readonly volatility: number;
}

export interface AnalyticsActionFrequencyRow {
  readonly action: string;
  readonly count: number;
  readonly share: number;
}

export interface AnalyticsAdvanced {
  readonly grossProfit: number;
  readonly grossLoss: number;
  readonly closedSessions: number;
  readonly winRate: number;
  readonly profitFactor: number | null;
  readonly monthlyRisk: readonly AnalyticsMonthlyRiskPoint[];
  readonly highRiskMonths: readonly string[];
  readonly highRiskInstrumentImpact: readonly AnalyticsInstrumentImpactRow[];
  readonly highRiskActionFrequency: readonly AnalyticsActionFrequencyRow[];
}

export interface AnalyticsPayload {
  readonly totalPnl: number;
  readonly cashFlow: number;
  readonly byDay: readonly AnalyticsByDayRow[];
  readonly byUnderlying: readonly AnalyticsByUnderlyingRow[];
  readonly topWinners: readonly AnalyticsByUnderlyingRow[];
  readonly topLosers: readonly AnalyticsByUnderlyingRow[];
  readonly streaks: AnalyticsStreaks;
  readonly performance: AnalyticsPerformance | null;
  readonly daily: {
    readonly equityCurve: readonly AnalyticsEquityPoint[];
    readonly drawdown: readonly AnalyticsDrawdownPoint[];
    readonly monthly: readonly AnalyticsMonthlyPoint[];
  };
  readonly advanced: AnalyticsAdvanced;
}

export interface UnderlyingTransaction {
  readonly id: string;
  readonly activity_date: string;
  readonly settle_date: string | null;
  readonly action: string;
  readonly quantity: number;
  readonly option_type: "call" | "put" | null;
  readonly strike: number | null;
  readonly expiration: string | null;
  readonly price: number | null;
  readonly amount: number | null;
  readonly description: string | null;
}

export interface UnderlyingDetailPayload {
  readonly underlying: string;
  readonly count: number;
  readonly activeDays: number;
  readonly pnl: number;
  readonly winRate: number;
  readonly cashFlow: number;
  readonly realizedBatches: readonly AnalyticsRealizedBatch[];
  readonly transactions: readonly UnderlyingTransaction[];
}

export const analytics = {
  getAnalytics: (opts?: { from?: string | null; to?: string | null }) => {
    const qs = new URLSearchParams();
    if (opts?.from) qs.set("from", opts.from);
    if (opts?.to) qs.set("to", opts.to);
    const path = qs.toString() ? `/analytics?${qs}` : "/analytics";
    return request<AnalyticsPayload>(path);
  },

  getUnderlyingDetail: (
    underlying: string,
    opts?: { from?: string | null; to?: string | null },
  ) => {
    const qs = new URLSearchParams({ underlying });
    if (opts?.from) qs.set("from", opts.from);
    if (opts?.to) qs.set("to", opts.to);
    return request<UnderlyingDetailPayload>(
      `/analytics/underlying?${qs.toString()}`,
    );
  },
};
